import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Calendar, UserRound, RefreshCcw, ExternalLink, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { SITE_URL } from "../SEO";

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.label, item: `${SITE_URL}${it.to}` })),
});

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Fil d'Ariane" data-testid="breadcrumbs" className="flex flex-wrap items-center gap-1.5 text-xs text-[#6B7280]">
    {items.map((it, i) => (
      <span key={it.to} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight size={12} strokeWidth={1.75} />}
        {i === items.length - 1 ? (
          <span className="text-[#0A1128] font-medium line-clamp-1">{it.label}</span>
        ) : (
          <Link to={it.to} className="hover:text-[#0A1128] transition-colors duration-200">{it.label}</Link>
        )}
      </span>
    ))}
  </nav>
);

export const GuideCard = ({ g, index }) => (
  <Link
    to={`/${g.slug}`}
    data-testid={`guide-card-${g.slug}`}
    className="group block bg-white border border-[#C4C8CC]/50 rounded-2xl p-7 shadow-[0_8px_32px_rgba(10,17,40,0.05)] hover:border-[#0A1128]/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
  >
    {index !== undefined && <span className="font-heading text-3xl text-[#C4C8CC]">{String(index + 1).padStart(2, "0")}</span>}
    <h3 className="text-base md:text-lg font-bold text-[#0A1128] mt-2 leading-snug group-hover:text-[#1A2342]">{g.title}</h3>
    <p className="text-sm text-[#6B7280] mt-3 leading-relaxed line-clamp-3">{g.meta_description}</p>
    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] mt-5">Lire le guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
  </Link>
);

export const GuideMeta = ({ g }) => {
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");
  return (
    <div data-testid="guide-meta" className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#6B7280]">
      <span className="inline-flex items-center gap-1.5"><UserRound size={13} strokeWidth={1.75} /> {g.author}</span>
      <span className="inline-flex items-center gap-1.5"><Calendar size={13} strokeWidth={1.75} /> Publié le {fmt(g.published_at)}</span>
      <span className="inline-flex items-center gap-1.5"><RefreshCcw size={13} strokeWidth={1.75} /> Mis à jour le {fmt(g.updated_at)}</span>
    </div>
  );
};

export const GuideFaq = ({ faq = [] }) => {
  const [open, setOpen] = useState(0);
  if (!faq.length) return null;
  return (
    <section data-testid="guide-faq" className="mt-16">
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A1128]">Questions fréquentes</h2>
      <div className="mt-6 divide-y divide-[#E5E7EB] border-t border-b border-[#E5E7EB]">
        {faq.map((f, i) => (
          <div key={f.q} className="py-5">
            <button data-testid={`guide-faq-toggle-${i}`} onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-start justify-between gap-6 text-left">
              <h3 className="text-base font-semibold text-[#0A1128]">{f.q}</h3>
              <span className="shrink-0 mt-0.5 text-[#6B7280]">{open === i ? <Minus size={18} /> : <Plus size={18} />}</span>
            </button>
            {open === i && <p className="text-[15px] text-[#4B5563] leading-relaxed mt-3 max-w-3xl">{f.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

export const GuideSources = ({ sources = [], disclaimer }) => (
  <section data-testid="guide-sources" className="mt-14 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl p-7">
    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280]">Sources officielles</h2>
    <ul className="mt-4 space-y-2">
      {sources.map((s) => (
        <li key={s.url}>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 text-sm text-[#0A1128] hover:underline underline-offset-4">
            <ExternalLink size={14} className="mt-0.5 shrink-0" /> {s.label}
          </a>
        </li>
      ))}
    </ul>
    {disclaimer && <p className="text-xs text-[#6B7280] leading-relaxed mt-5 border-t border-[#E5E7EB] pt-4">{disclaimer}</p>}
  </section>
);

export const RelatedGuides = ({ guides = [] }) => {
  if (!guides.length) return null;
  return (
    <section data-testid="related-guides" className="mt-16">
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A1128]">Guides liés</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {guides.slice(0, 6).map((g) => <GuideCard key={g.slug} g={g} />)}
      </div>
    </section>
  );
};

export const GuideCta = ({ title = "Besoin d'aide pour votre déclaration de frontalier ?", text = "Nos conseillers fiscaux analysent gratuitement votre situation Suisse-France et vous adressent un devis sans engagement." }) => (
  <div data-testid="guide-cta" className="mt-16 bg-[#0A1128] rounded-2xl p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
    <div>
      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-white">{title}</h2>
      <p className="text-sm text-[#9CA3AF] mt-2 max-w-xl">{text}</p>
    </div>
    <Link data-testid="guide-cta-devis" to="/devis" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] bg-white rounded-full px-7 py-3.5 hover:bg-[#E5E7EB] transition-colors duration-200 shrink-0">
      Demander un devis <ArrowRight size={16} />
    </Link>
  </div>
);
